import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useDraggable, useDroppable } from '@dnd-kit/core';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import useForkRef from '@mui/utils/useForkRef';
import { TreeItem } from '@mui/x-tree-view-pro';
import type { TreeItemProps } from '@mui/x-tree-view-pro';
import { FolderInput, FolderOpen, FolderRoot, Pencil, RefreshCw } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';

import { SKILL_FOLDER_ICON_STYLE, SkillTreeItemIcon } from './skill-file-icon';
import { SkillRemoveButton } from './skill-remove-button';
import { useSkillTreeRowsDrag } from './skill-tree-drag';
import { parseSkillTreeItemId } from './skill-tree-ids';
import type { SkillTreeItem } from './skill-tree-ids';
import { getIsRelocatableSkillTreeItem } from './skill-tree-relocation';
import type { RelocatableSkillTreeItem } from './skill-tree-relocation';

/** A folder the "Move to" menu offers, '' for the top level. */
export interface SkillMoveTarget {
  /** How deep the folder is, 0 for the top level. */
  depth: number;
  /** Why the item cannot move there, or null when it can. */
  error: string | null;
  folder: string;
  label: string;
}

/** A stored file still being processed, or one that could not be read. */
export type SkillTreeFileState =
  | { kind: 'processing' }
  | { kind: 'failed'; message: string; path: string };

/** What a row of the skill's file tree can do. */
export interface SkillTreeActions {
  editable: boolean;
  /** What the row's file is, for its icon; '' for a folder. */
  getContentType: (itemId: string) => string;
  /** The processing state of the row's stored file, or null when it is ready or not stored. */
  getFileState: (itemId: string) => SkillTreeFileState | null;
  /** Whether a folder holds any file, so removing an empty one can skip the confirmation. */
  getFolderHasFiles: (path: string) => boolean;
  /** Where the item can move: the top level, then every folder outside it, in tree order. */
  getMoveTargets: (item: RelocatableSkillTreeItem) => SkillMoveTarget[];
  /** Whether rows can be dragged now. Not while a name is being typed. */
  movable: boolean;
  move: (item: RelocatableSkillTreeItem, folder: string) => void;
  remove: (item: RelocatableSkillTreeItem) => void;
  rename: (item: RelocatableSkillTreeItem, name: string) => void;
  /** Tree id of the row being renamed. */
  renaming: string | null;
  /** Process a file that failed again. */
  retry: (path: string) => void;
}

/** The state of one inline name input: renaming a row, or naming a new file or folder. */
export interface SkillTreeNameInput {
  error: string;
  label: string;
  onBlur: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  value: string;
}

export const SkillTreeActionsContext = createContext<SkillTreeActions | null>(null);
export const SkillTreeRenameContext = createContext<SkillTreeNameInput | null>(null);
export const SkillTreeDraftContext = createContext<SkillTreeNameInput | null>(null);

const LABEL_STYLE: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 8,
  position: 'relative',
};
const LABEL_TEXT_STYLE: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const ACTIONS_STYLE: React.CSSProperties = { display: 'flex', flexShrink: 0 };
const FILE_STATE_STYLE: React.CSSProperties = {
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
};
const MOVE_MENU_SLOT_PROPS = { paper: { style: { maxHeight: 360, minWidth: 220 } } };
const NAME_INPUT_STYLE: React.CSSProperties = {
  border: '1px solid #1677FF',
  borderRadius: 3,
  font: 'inherit',
  fontSize: 14,
  minWidth: 0,
  outline: 'none',
  padding: '3px 6px',
  width: '100%',
};

/** The inline input for a name, with its error under it. */
const SkillTreeNameField = React.memo(function SkillTreeNameField({
  context,
}: {
  context: React.Context<SkillTreeNameInput | null>;
}) {
  const input = useContext(context);
  if (!input) throw new Error('SkillTreeNameField needs a name input context.');
  return (
    <>
      <input
        ref={focusAndSelect}
        aria-invalid={Boolean(input.error)}
        aria-label={input.label}
        style={NAME_INPUT_STYLE}
        value={input.value}
        onBlur={input.onBlur}
        onChange={input.onChange}
        onClick={stopPropagation}
        onKeyDown={input.onKeyDown}
      />
      {Boolean(input.error) && (
        <Typography color="error" role="alert" variant="caption">
          {input.error}
        </Typography>
      )}
    </>
  );
});

const SkillMoveTargetIcon = React.memo(function SkillMoveTargetIcon({
  folder,
}: {
  folder: string;
}) {
  if (!folder) return <FolderRoot size={16} />;
  return <FolderOpen size={16} style={SKILL_FOLDER_ICON_STYLE} />;
});

const SkillMoveTargetItem = React.memo(function SkillMoveTargetItem({
  onSelect,
  target,
}: {
  onSelect: (folder: string) => void;
  target: SkillMoveTarget;
}) {
  const select = useCallback(() => onSelect(target.folder), [onSelect, target.folder]);
  const style = useMemo(() => ({ paddingLeft: 16 + target.depth * 16 }), [target.depth]);
  return (
    <MenuItem disabled={Boolean(target.error)} style={style} onClick={select}>
      <ListItemIcon>
        <SkillMoveTargetIcon folder={target.folder} />
      </ListItemIcon>
      <ListItemText primary={target.label} secondary={target.error} />
    </MenuItem>
  );
});

/** Moves the item into a folder picked from a menu, for when dragging is not handy. */
const SkillMoveButton = React.memo(function SkillMoveButton({
  item,
  name,
}: {
  item: RelocatableSkillTreeItem;
  name: string;
}) {
  const { getMoveTargets, move } = useSkillTreeActions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const targets = useMemo(
    () => (anchor ? getMoveTargets(item) : []),
    [anchor, getMoveTargets, item]
  );
  const open = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  }, []);
  const close = useCallback(() => setAnchor(null), []);
  const select = useCallback(
    (folder: string) => {
      setAnchor(null);
      move(item, folder);
    },
    [item, move]
  );
  return (
    <>
      <AlineTooltip arrow placement="top" title={`Move ${name}`}>
        <IconButton
          aria-expanded={Boolean(anchor)}
          aria-haspopup="menu"
          aria-label={`Move ${name}`}
          size="small"
          onClick={open}
        >
          <FolderInput size={14} />
        </IconButton>
      </AlineTooltip>
      {/* The menu renders in a portal, but its events still bubble to the tree row. */}
      <Menu
        anchorEl={anchor}
        aria-label={`Move ${name} to`}
        open={Boolean(anchor)}
        slotProps={MOVE_MENU_SLOT_PROPS}
        onClick={stopPropagation}
        onClose={close}
        onKeyDown={stopPropagation}
      >
        {targets.map(target => (
          <SkillMoveTargetItem key={target.folder} target={target} onSelect={select} />
        ))}
      </Menu>
    </>
  );
});

const SkillTreeItemActions = React.memo(function SkillTreeItemActions({
  item,
  name,
}: {
  item: RelocatableSkillTreeItem;
  name: string;
}) {
  const { getFolderHasFiles, remove, rename } = useSkillTreeActions();
  const startRename = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      rename(item, name);
    },
    [item, name, rename]
  );
  const confirmRemove = useCallback(() => remove(item), [item, remove]);
  const isFolder = item.kind === 'folder';
  return (
    <span className="skill-tree-actions" style={ACTIONS_STYLE}>
      <SkillMoveButton item={item} name={name} />
      <AlineTooltip arrow placement="top" title={`Rename ${name}`}>
        <IconButton aria-label={`Rename ${name}`} size="small" onClick={startRename}>
          <Pencil size={14} />
        </IconButton>
      </AlineTooltip>
      <SkillRemoveButton
        confirm={!isFolder || getFolderHasFiles(item.path)}
        folder={isFolder}
        name={name || 'file'}
        onConfirm={confirmRemove}
      />
    </span>
  );
});

const SkillTreeItemText = React.memo(function SkillTreeItemText({
  item,
  itemId,
  name,
}: {
  item: SkillTreeItem | null;
  itemId: string;
  name: string;
}) {
  const actions = useSkillTreeActions();
  if (item?.kind === 'folderDraft' || item?.kind === 'fileDraft')
    return <SkillTreeNameField context={SkillTreeDraftContext} />;
  if (actions.renaming === itemId) return <SkillTreeNameField context={SkillTreeRenameContext} />;
  return <>{name}</>;
});

const SkillTreeProcessingIndicator = React.memo(function SkillTreeProcessingIndicator({
  name,
}: {
  name: string;
}) {
  return (
    <AlineTooltip arrow placement="top" title="Processing this file">
      <span aria-label={`Processing ${name}`} role="status" style={FILE_STATE_STYLE}>
        <PulseIcon variant={PulseIconVariant.LoadingSecondary} />
      </span>
    </AlineTooltip>
  );
});

/** An error pulse with the reason, and a menu to process the file again. */
const SkillTreeFailedIndicator = React.memo(function SkillTreeFailedIndicator({
  message,
  name,
  path,
}: {
  message: string;
  name: string;
  path: string;
}) {
  const { editable, retry } = useSkillTreeActions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  }, []);
  const close = useCallback(() => setAnchor(null), []);
  const retryFile = useCallback(() => {
    setAnchor(null);
    retry(path);
  }, [path, retry]);
  return (
    <>
      <AlineTooltip arrow placement="top" title={message}>
        <IconButton
          aria-expanded={Boolean(anchor)}
          aria-haspopup="menu"
          aria-label={`${name} could not be read`}
          disabled={!editable}
          size="small"
          onClick={open}
        >
          <PulseIcon animate={false} variant={PulseIconVariant.Error} />
        </IconButton>
      </AlineTooltip>
      {/* The menu renders in a portal, but its events still bubble to the tree row. */}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClick={stopPropagation}
        onClose={close}
        onKeyDown={stopPropagation}
      >
        <MenuItem onClick={retryFile}>
          <ListItemIcon>
            <RefreshCw size={16} />
          </ListItemIcon>
          <ListItemText primary="Retry" />
        </MenuItem>
      </Menu>
    </>
  );
});

const SkillTreeItemLabel = React.memo(function SkillTreeItemLabel({
  itemId,
  name,
}: {
  itemId: string;
  name: string;
}) {
  const actions = useSkillTreeActions();
  const item = useMemo(() => parseSkillTreeItemId(itemId), [itemId]);
  const isFolder = item?.kind === 'folder' || item?.kind === 'folderDraft';
  const relocatable = item && getIsRelocatableSkillTreeItem(item) ? item : null;
  const fileState = actions.getFileState(itemId);
  return (
    <span style={LABEL_STYLE}>
      <SkillTreeItemIcon
        contentType={actions.getContentType(itemId)}
        isFolder={isFolder}
        size={20}
      />
      <AlineTooltip arrow placement="top" title={name}>
        <span style={LABEL_TEXT_STYLE}>
          <SkillTreeItemText item={item} itemId={itemId} name={name} />
        </span>
      </AlineTooltip>
      {fileState?.kind === 'processing' && <SkillTreeProcessingIndicator name={name} />}
      {fileState?.kind === 'failed' && (
        <SkillTreeFailedIndicator message={fileState.message} name={name} path={fileState.path} />
      )}
      {actions.editable && relocatable && actions.renaming !== itemId && (
        <SkillTreeItemActions item={relocatable} name={name} />
      )}
    </span>
  );
});

/**
 * A row of the skill's file tree, with its actions. The whole row drags. A drop on a folder's
 * row moves into that folder, and a drop on a file's row into the file's folder.
 */
export const SkillTreeRow = React.memo(
  React.forwardRef<HTMLLIElement, TreeItemProps>(function SkillTreeRow(
    { className, itemId, label, ...props },
    ref
  ) {
    const actions = useSkillTreeActions();
    const { draggedIds, dropTargetId } = useSkillTreeRowsDrag();
    const item = useMemo(() => parseSkillTreeItemId(itemId), [itemId]);
    const isDraft = item?.kind === 'fileDraft' || item?.kind === 'folderDraft';
    const draggable = useDraggable({
      disabled: !actions.movable || !item || !getIsRelocatableSkillTreeItem(item),
      id: itemId,
    });
    const droppable = useDroppable({ disabled: !item || isDraft, id: itemId });
    const handleRef = useForkRef(ref, draggable.setNodeRef, droppable.setNodeRef);
    const name = typeof label === 'string' ? label : '';
    const classNames = [
      className,
      draggedIds.has(itemId) && 'skill-tree-dragging',
      dropTargetId === itemId && 'skill-tree-drop-target',
    ].filter(Boolean);
    return (
      <TreeItem
        {...props}
        {...draggable.listeners}
        ref={handleRef}
        className={classNames.join(' ')}
        itemId={itemId}
        label={<SkillTreeItemLabel itemId={itemId} name={name} />}
      />
    );
  })
);

export const SKILL_TREE_SLOTS = { item: SkillTreeRow };

function useSkillTreeActions(): SkillTreeActions {
  const actions = useContext(SkillTreeActionsContext);
  if (!actions) throw new Error('useSkillTreeActions must be used inside SkillTreeActionsContext.');
  return actions;
}

function focusAndSelect(input: HTMLInputElement | null): void {
  input?.focus();
  input?.select();
}

function stopPropagation(event: React.SyntheticEvent): void {
  event.stopPropagation();
}
